import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AppRun } from '../../../../hooks/useAppRun';
import { IUserInput } from '../../../../../generated/gql/graphql';
import {
  isMessage,
  isStatusMessage,
  useClientStore
} from '../../../../hooks/ClientState';
import {
  Box,
  CircularProgress, Stack, useTheme,
  Alert,
  Button,
  Typography,
  IconButton,
  Fade
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import { Pulse } from '../../../../components/animations';
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import Markdown from "../../../../components/material-markdown";
import ChatBubble from "./ChatBubble";
import ChatInput from './ChatInput';
import { useShallow } from 'zustand/react/shallow';
import { InterruptedReason } from '../../../../types/ClientState';
import { alpha } from '@mui/material/styles';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';

// Component for expandable status messages
const ExpandableStatusMessage = ({ content, idx, onExpand }: {
  content: string,
  idx: number,
  onExpand?: () => void  // Add callback prop
}) => {
  const [expanded, setExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const theme = useTheme();

  // Check if content needs expansion option
  useLayoutEffect(() => {
    // Content needs expansion if:
    // 1. It has more than one line OR
    // 2. It's longer than 75 characters
    const hasMultipleLines = content.includes('\n');
    const exceedsMaxChars = content.length > 75;

    setNeedsExpansion(hasMultipleLines || exceedsMaxChars);
  }, [content]);

  const toggleExpanded = () => {
    if (needsExpansion) {
      const newExpandedState = !expanded;
      setExpanded(newExpandedState);

      // Notify parent about expansion change
      if (onExpand) {
        // Use setTimeout to ensure the DOM has updated before checking position
        setTimeout(onExpand, 0);
      }
    }
  };

  // Status message styling applied to all views
  const statusMessageStyles = {
    color: theme.palette.text.secondary,
    fontWeight: 500,  // Medium weight - slightly heavier than normal
    fontSize: '0.95rem', // Slightly smaller than regular text
  };

  // If the message doesn't need expansion, just render it normally
  if (!needsExpansion) {
    return (
      <Box
        pl={2}
        key={idx}
        maxWidth='75ch'
        width="100%"
        sx={{
          ...statusMessageStyles,
        }}
      >
        <Markdown>{content}</Markdown>
      </Box>
    );
  }

  return (
    <Box
      pl={2}
      key={idx}
      maxWidth='75ch'
      width="100%"
      sx={{
        position: 'relative',
        ...statusMessageStyles,
      }}
    >
      {expanded ? (
        // Expanded state with full content - entire box is clickable
        <Box
          onClick={toggleExpanded}
          sx={{
            width: '100%',
            overflowWrap: 'break-word',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            hyphens: 'auto',
            maxWidth: '100%',
            cursor: 'pointer',
            ...statusMessageStyles,
          }}
        >
          <Markdown>{content}</Markdown>

          <Button
            fullWidth
            size="small"
            startIcon={<ExpandLessRoundedIcon />}
            sx={{
              mt: 1,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.secondary, 0.1),
              }
            }}
          >
            Show less
          </Button>
        </Box>
      ) : (
        // Collapsed state with ellipsis and expand icon
        <Box
          onClick={toggleExpanded}
          sx={{
            cursor: 'pointer',
            position: 'relative',
            paddingRight: '32px', // Space for the expand icon and ellipsis
          }}
        >
          <Box
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: 'calc(100% - 36px)', // Make room for the icon and ellipsis
              maxHeight: '1.5em',
              '& *': {
                overflow: 'hidden',
              }
            }}
          >
            <Markdown>{content}</Markdown>
          </Box>

          {/* Expand icon for collapsed state */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              right: 0,
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Stack direction='row' spacing={1}>
              <MoreHorizRoundedIcon
                fontSize="small"
                color="action"
              />
              <ExpandMoreRoundedIcon
                fontSize="small"
                color="action"
              />
            </Stack>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export function ChatView(props: AppRun & { contentMaxWidth: string | number }): React.ReactElement {
  const [input, setInput] = useState<IUserInput>({});
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [
    interrupted,
    setInterrupted,
  ] = useClientStore(useShallow(state => [
    state.interrupted,
    state.setInterrupted,
  ]));
  const containerRef = React.useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const [
    chatEntries, completed, initializing, inputRequirement,
  ] = useClientStore(useShallow(state => [
    state.chatEntries,
    state.completed,
    state.initializing,
    state.inputRequirement,
  ]));

  // Extract check bottom logic into a reusable function
  const checkIfAtBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // Check if scrolled to bottom (with a small threshold for rounding errors)
    const isBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 16;
    setIsAtBottom(isBottom);
  }, []);

  // Handle scroll events to determine if we're at the bottom
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', checkIfAtBottom);
    checkIfAtBottom(); // Check initial position

    return () => {
      container.removeEventListener('scroll', checkIfAtBottom);
    };
  }, [checkIfAtBottom]);

  // Use a more focused approach to track scrollable height
  // Instead of watching all mutations, just use ResizeObserver on the container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Track previous scrollHeight to detect changes
    let prevScrollHeight = container.scrollHeight;

    // Create a ResizeObserver to detect height changes in the container
    const resizeObserver = new ResizeObserver(() => {
      // Only update if the scrollHeight actually changed
      if (container.scrollHeight !== prevScrollHeight) {
        prevScrollHeight = container.scrollHeight;
        checkIfAtBottom();
      }
    });

    // Only observe the container itself
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [checkIfAtBottom]);

  // Also check if we're at bottom when chat entries change
  useEffect(() => {
    checkIfAtBottom();
  }, [chatEntries, checkIfAtBottom]);

  const scrollToBottom = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  // Auto-scroll to bottom when new messages arrive
  // NOTE: auto-scroll for input requirement change is triggered by the chat input spacer height update
  useEffect(scrollToBottom, [chatEntries, props.inProgress, completed]);

  const inputBoxRef = React.useRef<HTMLDivElement | null>(null);
  const [inputBoxHeight, setInputBoxHeight] = useState(0);
  // Create debounced set height function
  const debouncedSetHeight = useMemo(
    () =>
      debounce((height: number) => {
        setInputBoxHeight(height);
      }, 200),
    []
  );
  const inputRequirementChanged = useRef(false);

  useEffect(() => {
    inputRequirementChanged.current = true;
  }, [inputRequirement]);


  // Measure immediately and whenever the box is resized
  useLayoutEffect(() => {
    const el = inputBoxRef.current;
    if (!el) return;

    // Initial measurement - no debounce for first measurement
    setInputBoxHeight(el.offsetHeight);

    // Listen for future changes
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        // Use debounced function for height updates
        // we need the debounce to avoid changing the state multiple times on single UserRequirement change
        debouncedSetHeight(entry.contentRect.height);
      }
    });
    ro.observe(el);

    return () => {
      debouncedSetHeight.clear(); // Clean up debounced function
      ro.disconnect();
    };
  }, []);

  // Scroll to bottom when input box height changes due to input requirement change
  // NOTE trigger this with useEffect to make sure it happens after spacer rerender
  useEffect(() => {
    // NOTE we are relying on the assumption that only single inputBoxHeight state change per inputRequirement change
    // this should be guaranteed by the debounce of setting the height when the input box is resized
    if (inputRequirementChanged.current) {
      scrollToBottom();
      inputRequirementChanged.current = false;
    }
  }, [inputBoxHeight]);

  // small overlap of input box allowed when conversation is scrolled up to create floating effect
  const overlapPx = inputBoxHeight;

  return initializing
    ? <Box maxWidth={props.contentMaxWidth} width='100%' height='100%' display='flex' justifyContent='center' alignItems='center'>
      <CircularProgress size='5rem' />
    </Box>
    : <Stack
      position='relative'
      width='100%'
      height='100%'
      overflow='hidden'
    >
      {/* Conversation container that extends full height */}
      <Box
        ref={containerRef}
        sx={{
          flexShrink: 1,
          pb: `${overlapPx + 16}px`, // Padding bottom to avoid content being covered by input box when it's scrolled to bottom
          overflowY: 'auto',
          scrollBehavior: 'smooth', // Add smooth scrolling behavior
          // gutter space on both side to keep content centered
          scrollbarGutter: 'stable both-edges',
          // Custom scrollbar styling
          '&::-webkit-scrollbar': {
            width: {
              xs: 0, // Hide on small screens
              sm: 1, // Medium size on tablet
              lg: 2, // Larger on desktop
            },
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            borderRadius: '10px',
            margin: 1,
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.2)'
              : 'rgba(0, 0, 0, 0.2)',
            borderRadius: '10px',
            '&:hover': {
              background: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.3)'
                : 'rgba(0, 0, 0, 0.3)',
            }
          },
          // Standard scrollbar for Firefox and modern browsers
          scrollbarWidth: {
            xs: 'none', // Hide on small screens
            sm: 'thin', // Visible on larger screens
          },
          scrollbarColor: theme =>
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.2) transparent'
              : 'rgba(0, 0, 0, 0.2) transparent',
        }}
      >
        <Stack
          spacing={2}
          width='100%'
          mx='auto'
          maxWidth={props.contentMaxWidth}
        >
          {chatEntries.map(
            (entry, idx) => isStatusMessage(entry)
              ? <ExpandableStatusMessage
                key={idx}
                content={entry.content}
                idx={idx}
                onExpand={checkIfAtBottom}  // Pass the callback
              />
              : isMessage(entry)
                ? <ChatBubble
                  key={idx}
                  message={entry} />
                : <Alert key={idx} severity='error'>
                  <Typography variant='subtitle2'>{entry.name}</Typography>
                  <Typography variant='body2'><pre>{entry.message}</pre></Typography>
                </Alert>
          )}
          {props.inProgress ? <Box p={1}><Pulse><MoreHorizIcon color='secondary' fontSize='large' /></Pulse></Box> : undefined}
        </Stack>
      </Box>

      {/* Spacer to avoid input box covering content, minus small overlap */}
      <Box height={`${inputBoxHeight - overlapPx}px`} flexShrink={0} />

      {/* Scroll to bottom button */}
      <Fade in={!isAtBottom}>
        <Box
          sx={{
            position: 'absolute',
            bottom: '120px', // leave the space to clear the chat input box (additionalInput is expceted to be hidden)
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
          }}
        >
          <IconButton
            size="small"
            onClick={scrollToBottom}
            sx={{
              backgroundColor: alpha(theme.palette.secondary.main, 0.5),
              color: theme.palette.getContrastText(theme.palette.secondary.main),
              '&:hover': {
                backgroundColor: alpha(theme.palette.secondary.main, 0.8),
              },
              boxShadow: 2,
              width: 36,
              height: 36,
              transition: 'all 0.2s ease',
            }}
          >
            <ArrowDownwardRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Fade>

      {/* Fixed position container for input at bottom */}
      <Box
        ref={inputBoxRef}
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          maxWidth: props.contentMaxWidth,
          width: '100%',
          mx: 'auto',
          pointerEvents: 'none', // Prevent pointer events on the container itself
        }}
      >
        {completed
          ? <Box width='100%' p={1} display='flex' justifyContent='center' alignItems='center'>
            <Typography variant='subtitle1' color={theme.palette.text.secondary}>Session has ended.</Typography>
          </Box>
          : interrupted && <Button variant='contained'
            color='secondary'
            sx={{
              width: '100%',
              borderRadius: '40px',
              boxShadow: theme.shadows[5],
              pointerEvents: 'auto',
              textTransform: 'none',
              transform: 'translateY(-4px)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            onClick={() => {
              setInterrupted(null);
              props.send([], () => setInput({}));
            }}
          >
            {interrupted === 'stop'
              ? <Stack direction='row' spacing={1} alignItems='center'>
                <PlayArrowRoundedIcon />
                <Typography variant="h6">Resume</Typography>
              </Stack>
              : <Stack direction='row' spacing={1} alignItems='center'>
                <ReplayRoundedIcon />
                <Typography variant="h6">Retry</Typography>
              </Stack>
            }
          </Button>
        }
        <ChatInput
          key='chat-input'
          processing={props.inProgress}
          disabled={props.inProgress}
          value={input}
          inputRequirment={inputRequirement || defaultInputRequirement}
          onChange={setInput}
          onSubmit={s => props.send([s], () => setInput({}))}
          onCancel={() => {
            props.disconnect(true);
            setInterrupted('stop');
          }}
          isAtBottom={isAtBottom}
          hidden={completed || interrupted !== null}
        />
      </Box>
    </Stack>;
}

const defaultInputRequirement = {
  choicesOnly: false,
  choicesMultiple: false,
  knowledgeSelect: false,
  datasetSelect: false,
  fileSelect: false,
  webInteractionOutcome: false,
  runAppClientId: false,
}
